import { useMemo } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
  ComplianceProgramStep,
} from "types/graphql-schema";

import { getReviewQuestionScope } from "../review-questions/review-question-rows";
import { ProgramForm, ProgramFormStep } from "../types";
import { toQuestionSetOption } from "./question-set-option";
import QuestionSetSelector from "./question-set-selector";
import { QuestionList } from "./question-tab-section";

const EngagementSurveyStep = ({
  proposedComplianceProgram,
  canUpdate,
  publishedReviewId,
  availableAssessmentGroups,
  onAddOrUpdateStep,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  showQuestionSetError,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  publishedReviewId: number | null;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  showQuestionSetError: boolean;
}) => {
  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) =>
          assessmentType === AssessmentType.EngagementSurvey,
      )
      .map(toQuestionSetOption);
  }, [availableAssessmentGroups]);

  const engagementSurveyStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.EngagementSurvey,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return engagementSurveyStep &&
      engagementSurveyStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === engagementSurveyStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, engagementSurveyStep]);

  return (
    engagementSurveyStep && (
      <QuestionList
        title="Survey"
        items={[
          {
            key: "question-set",
            added: !!selectedAssessmentGroup,
            row: (
              <QuestionSetSelector
                review={getReviewQuestionScope(
                  publishedReviewId,
                  engagementSurveyStep,
                )}
                assessmentType={AssessmentType.EngagementSurvey}
                availableAssessmentGroupSelectOptions={
                  availableAssessmentGroupSelectOptions
                }
                canUpdate={canUpdate}
                onUpdateAssessmentTemplate={(assessmentGroupId) => {
                  onAddOrUpdateStep({
                    ...engagementSurveyStep,
                    questionSetIds: assessmentGroupId
                      ? [assessmentGroupId]
                      : [],
                  });
                }}
                onAssessmentGroupCreated={onAssessmentGroupCreated}
                onAssessmentGroupArchived={onAssessmentGroupArchived}
                selectedAssessmentGroup={selectedAssessmentGroup}
                error={
                  showQuestionSetError &&
                  engagementSurveyStep.questionSetIds.length === 0
                    ? "Please choose a question set"
                    : undefined
                }
              />
            ),
          },
        ]}
      />
    )
  );
};
export default EngagementSurveyStep;
